import React, { useEffect, useState, useCallback } from "react";
import PanditApiService from "../../services/panditApiService";
import { toast } from "react-toastify";

/* ─── CONFIG ─────────────────────────────────────────────────── */
const BOOKING_STATUS_CFG = {
  acharya_assigned:  { label: "Assigned",      color: "#8b5cf6", bg: "#f3e8ff" },
  samagri_delivered: { label: "Samagri Ready",  color: "#f97316", bg: "#fff7ed" },
  puja_started:      { label: "Puja Started",   color: "#3b82f6", bg: "#eff6ff" },
  puja_completed:    { label: "Completed",       color: "#22c55e", bg: "#f0fdf4" },
};

const CHECKIN_CFG = {
  CHECKED_IN:  { label: "Checked In",  color: "#22c55e", icon: "fa-check-circle"  },
  CHECKED_OUT: { label: "Checked Out", color: "#6b7280", icon: "fa-sign-out"      },
  null:        { label: "Not Checked In", color: "#ef4444", icon: "fa-times-circle" },
};

const OTP_CFG = {
  PENDING:  { label: "OTP Pending",  color: "#f97316", icon: "fa-clock-o"      },
  VERIFIED: { label: "OTP Verified", color: "#22c55e", icon: "fa-check-circle" },
  null:     { label: "—",            color: "#9ca3af", icon: "fa-minus"        },
};

/* ─── TABLE CELL STYLES ─────────────────────────────────────── */
const TH = {
  padding: "10px 14px", fontSize: "11px", fontWeight: 600, color: "#6b7280",
  textTransform: "uppercase", letterSpacing: "0.4px",
  borderBottom: "1px solid #e9ecef", whiteSpace: "nowrap", background: "#f9fafb",
};
const TD = { padding: "10px 14px", verticalAlign: "middle", borderBottom: "1px solid #f5f5f5" };

/* ─── HELPERS ────────────────────────────────────────────────── */
function formatDate(d) {
  if (!d) return "—";
  return new Date(d).toLocaleString("en-GB", {
    timeZone: "Asia/Kolkata", day: "2-digit", month: "short", year: "numeric",
    hour: "2-digit", minute: "2-digit", hour12: true,
  });
}

/* ─── COMPONENT ──────────────────────────────────────────────── */
export default function PujaControl() {
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(false);
  const [actionInProgress, setActionInProgress] = useState(null);
  const [statusFilter, setStatusFilter] = useState("all");
  const [search, setSearch] = useState("");

  const fetchBookings = useCallback(async () => {
    setLoading(true);
    try {
      const res = await PanditApiService.get("/internal/pandits/admin/bookings");
      setBookings(res?.data || []);
    } catch {
      toast.error("Failed to fetch active bookings");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchBookings(); }, [fetchBookings]);

  const forceCheckIn = async (bookingId) => {
    const confirmed = window.confirm(`Force check-in for booking ${bookingId}?`);
    if (!confirmed) return;
    setActionInProgress(`checkin-${bookingId}`);
    try {
      await PanditApiService.post(`/internal/pandits/admin/bookings/${bookingId}/force-checkin`);
      toast.success("Check-in forced successfully");
      fetchBookings();
    } catch (err) {
      toast.error(err?.response?.data?.message || "Force check-in failed");
    } finally {
      setActionInProgress(null);
    }
  };

  const forceComplete = async (bookingId) => {
    const confirmed = window.confirm(`Mark puja ${bookingId} as complete?`);
    if (!confirmed) return;
    setActionInProgress(`complete-${bookingId}`);
    try {
      await PanditApiService.post(`/internal/pandits/admin/bookings/${bookingId}/force-complete`);
      toast.success("Puja marked as complete");
      // Optimistically update local state — the API only returns active statuses so
      // re-fetching would silently drop the now-completed row from the table.
      setBookings(prev => prev.map(b =>
        b.bookingId === bookingId
          ? {
              ...b,
              bookingStatus: 'puja_completed',
              checkIn: {
                ...(b.checkIn || {}),
                checkInStatus:       'CHECKED_IN',
                completionOtpStatus: 'VERIFIED',
                checkoutTime:        new Date().toISOString(),
              },
            }
          : b
      ));
    } catch (err) {
      toast.error(err?.response?.data?.message || "Force complete failed");
    } finally {
      setActionInProgress(null);
    }
  };

  const filtered = bookings.filter(b => {
    if (statusFilter !== "all" && b.bookingStatus !== statusFilter) return false;
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      const inId       = b.bookingId?.toLowerCase().includes(q);
      const inPandit   = b.pandit?.name?.toLowerCase().includes(q);
      const inPuja     = b.service?.title?.toLowerCase().includes(q);
      const inCustomer = b.customer?.name?.toLowerCase().includes(q);
      if (!inId && !inPandit && !inPuja && !inCustomer) return false;
    }
    return true;
  });

  return (
    <div>
      {/* Page Title */}
      <div style={{ marginBottom: 16 }}>
        <h4 style={{ fontWeight: 700, color: "#111827", margin: "0 0 2px" }}>Puja Control</h4>
        <p style={{ color: "#6b7280", fontSize: "13px", margin: 0 }}>
          Manage active puja check-in and checkout status
        </p>
      </div>

      {/* Table Card */}
      <div style={{ background: "#fff", border: "1px solid #e9ecef", borderRadius: 10, overflow: "hidden" }}>

        {/* Toolbar */}
        <div style={{
          padding: "12px 16px", borderBottom: "1px solid #f0f0f0",
          display: "flex", alignItems: "center", justifyContent: "space-between",
        }}>
          <h5 style={{ margin: 0, fontWeight: 700, fontSize: "15px", color: "#111827" }}>
            Active Pujas
            {!loading && (
              <span style={{ fontSize: "12px", color: "#6b7280", fontWeight: 400, marginLeft: 6 }}>
                ({filtered.length})
              </span>
            )}
          </h5>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <div className="input-group input-group-sm" style={{ width: 260 }}>
              <input
                type="text"
                className="form-control"
                placeholder="Search booking ID, acharya, puja, customer..."
                value={search}
                onChange={e => setSearch(e.target.value)}
              />
              {search && (
                <button className="btn btn-outline-secondary" onClick={() => setSearch("")} style={{ padding: "0 8px" }}>
                  ✕
                </button>
              )}
            </div>
            <button
              className="btn btn-sm btn-outline-secondary"
              onClick={fetchBookings}
              disabled={loading}
              style={{ fontSize: "12px" }}
            >
              <i className={`fa ${loading ? "fa-spinner fa-spin" : "fa-refresh"}`} style={{ marginRight: 5 }} />
              Refresh
            </button>
          </div>
        </div>

        {/* Status Filter Pills */}
        <div style={{ padding: "10px 16px", borderBottom: "1px solid #f0f0f0", display: "flex", gap: 8, flexWrap: "wrap" }}>
          {[{ key: "all", label: "All", color: "#374151", bg: "#f3f4f6" }, ...Object.entries(BOOKING_STATUS_CFG).map(([key, cfg]) => ({ key, label: cfg.label, color: cfg.color, bg: cfg.bg }))].map(({ key, label, color, bg }) => {
            const count = key === "all" ? bookings.length : bookings.filter(b => b.bookingStatus === key).length;
            const active = statusFilter === key;
            return (
              <button
                key={key}
                onClick={() => setStatusFilter(key)}
                style={{
                  border: `1px solid ${active ? color : "#e5e7eb"}`,
                  background: active ? bg : "#fff",
                  color: active ? color : "#6b7280",
                  borderRadius: 20, padding: "4px 12px", fontSize: "12px",
                  fontWeight: active ? 600 : 400, cursor: "pointer",
                  display: "flex", alignItems: "center", gap: 6, transition: "all 0.15s",
                }}
              >
                {label}
                <span style={{
                  background: active ? color : "#e5e7eb",
                  color: active ? "#fff" : "#6b7280",
                  borderRadius: 10, padding: "0 6px", fontSize: "10px", fontWeight: 700, lineHeight: "18px",
                }}>
                  {count}
                </span>
              </button>
            );
          })}
        </div>

        {/* Table */}
        <div style={{ overflowX: "auto" }}>
          <table className="table mb-0" style={{ fontSize: "13px" }}>
            <thead>
              <tr>
                <th style={TH}>Booking ID</th>
                <th style={TH}>Puja</th>
                <th style={TH}>Puja Date/Time</th>
                <th style={TH}>Pandit</th>
                <th style={TH}>Customer</th>
                <th style={TH}>Booking Status</th>
                <th style={TH}>Check-In</th>
                <th style={TH}>OTP Status</th>
                <th style={TH}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={9} className="text-center py-4" style={{ color: "#9ca3af" }}>
                    <i className="fa fa-spinner fa-spin" style={{ marginRight: 6 }} />Loading...
                  </td>
                </tr>
              ) : filtered.length === 0 ? (
                <tr>
                  <td colSpan={9} className="text-center py-4" style={{ color: "#9ca3af" }}>
                    {bookings.length === 0 ? "No active pujas at the moment" : "No pujas match the selected filter"}
                  </td>
                </tr>
              ) : filtered.map(b => {
                const bsCfg = BOOKING_STATUS_CFG[b.bookingStatus] || {};
                const ciKey = b.checkIn?.checkInStatus || "null";
                const ciCfg = CHECKIN_CFG[ciKey] || CHECKIN_CFG["null"];
                const otpKey = b.checkIn?.completionOtpStatus || "null";
                const otpCfg = OTP_CFG[otpKey] || OTP_CFG["null"];

                const canCheckIn = ["acharya_assigned", "samagri_delivered"].includes(b.bookingStatus)
                  && b.checkIn?.checkInStatus !== "CHECKED_IN";
                const canComplete = b.bookingStatus === "puja_started"
                  && b.checkIn?.completionOtpStatus !== "VERIFIED";
                const isCompleted = b.bookingStatus === "puja_completed"
                  || b.checkIn?.completionOtpStatus === "VERIFIED";
                const actionKey = canCheckIn ? `checkin-${b.bookingId}` : `complete-${b.bookingId}`;
                const busy = actionInProgress === actionKey;

                return (
                  <tr key={b.bookingId}>
                    <td style={TD}>
                      <span style={{ fontFamily: "monospace", fontSize: "11px", color: "#6b7280" }}>
                        {b.bookingId}
                      </span>
                    </td>
                    <td style={TD}>
                      <span style={{ fontWeight: 500, color: "#111827" }}>{b.service?.title || "—"}</span>
                    </td>
                    <td style={TD}>
                      <span style={{ fontSize: "12px", color: "#374151" }}>{formatDate(b.pujaDateTime)}</span>
                    </td>
                    <td style={TD}>
                      <div style={{ fontWeight: 500, color: "#111827" }}>{b.pandit?.name || "—"}</div>
                      {b.pandit?.phone && (
                        <div style={{ fontSize: "11px", color: "#9ca3af" }}>{b.pandit.phone}</div>
                      )}
                    </td>
                    <td style={TD}>
                      <div style={{ fontWeight: 500, color: "#111827" }}>{b.customer?.name || "—"}</div>
                      {b.customer?.phone && (
                        <div style={{ fontSize: "11px", color: "#9ca3af" }}>{b.customer.phone}</div>
                      )}
                    </td>
                    <td style={TD}>
                      <span style={{
                        display: "inline-flex", alignItems: "center", gap: 4,
                        padding: "3px 8px", borderRadius: 12, fontSize: "11px", fontWeight: 600,
                        color: bsCfg.color, background: bsCfg.bg,
                      }}>
                        {bsCfg.label || b.bookingStatus}
                      </span>
                    </td>
                    <td style={TD}>
                      <div style={{ display: "flex", alignItems: "center", gap: 5 }}>
                        <i className={`fa ${ciCfg.icon}`} style={{ color: ciCfg.color, fontSize: 13 }} />
                        <span style={{ fontSize: "11px", color: ciCfg.color, fontWeight: 500 }}>{ciCfg.label}</span>
                      </div>
                      {b.checkIn?.checkInTime && (
                        <div style={{ fontSize: "10px", color: "#9ca3af", marginTop: 2 }}>
                          {formatDate(b.checkIn.checkInTime)}
                        </div>
                      )}
                    </td>
                    <td style={TD}>
                      <div style={{ display: "flex", alignItems: "center", gap: 5 }}>
                        <i className={`fa ${otpCfg.icon}`} style={{ color: otpCfg.color, fontSize: 13 }} />
                        <span style={{ fontSize: "11px", color: otpCfg.color, fontWeight: 500 }}>{otpCfg.label}</span>
                      </div>
                      {b.checkIn?.checkoutTime && (
                        <div style={{ fontSize: "10px", color: "#9ca3af", marginTop: 2 }}>
                          {formatDate(b.checkIn.checkoutTime)}
                        </div>
                      )}
                    </td>
                    <td style={TD}>
                      {isCompleted ? (
                        <span style={{ fontSize: "11px", color: "#22c55e", fontWeight: 600 }}>
                          <i className="fa fa-check-circle" style={{ marginRight: 4 }} />Done
                        </span>
                      ) : (
                        <button
                          className={`btn btn-xs ${canCheckIn ? "btn-outline-primary" : "btn-outline-success"}`}
                          disabled={busy || (!canCheckIn && !canComplete)}
                          onClick={() => canCheckIn ? forceCheckIn(b.bookingId) : forceComplete(b.bookingId)}
                        >
                          {busy
                            ? <i className="fa fa-spinner fa-spin" />
                            : canCheckIn ? "Check In" : "Complete"}
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
